<template>
  <div :class="parentClass">
    <Combobox v-model="model" @update:modelValue="itemSelected" nullable>
      <div>
        <p
          class="block w-full text-sm font-semibold leading-tight text-neutral-800 dark:text-grey-200"
          :class="{ '!text-theme-disabled dark:!text-grey-500': disabled }"
        >
          {{ label }}
          <span
            v-if="required"
            class="mt-1 text-sm font-semibold leading-tight ml-1/5 text-danger-500"
          >
            *
          </span>
        </p>
        <div
          v-bind="$attrs"
          :class="[
            inputClasses[theme],
            { 'pointer-events-none !bg-grey-100 dark:!bg-grey-700': disabled },
          ]"
          class="relative !w-full py-1 pl-12 shadow-none cursor-default overflow-hidden rounded-md border border-theme-divider pr-5 text-left font-bold outline-none sm:text-sm dark:bg-transparent dark:border-grey-700"
        >
          <ComboboxInput
            autocomplete="off"
            :placeholder="placeholder"
            :class="{ 'pointer-events-none bg-grey-100 dark:bg-dark-neutral': disabled }"
            class="w-full h-full px-0 py-0 pr-3 text-xs font-normal border-none shadow-none bg-inherit text-theme-primary"
            :displayValue="resolveDisplayValue"
            @change="handleInput"
            @keydown.enter.prevent="callQuerySearch($event.target.value)"
            @focus.stop
          />
          <!-- <ComboboxButton
            v-if="showDropdownIcon"
            class="absolute inset-y-0 right-0 flex items-center flex-shrink-0 pr-2 dark:text-grey-400"
            @click="
              query = $event.target.value;
              fetchSuggestions && !fetchedOptions.length && fetchRemote($event);
            "
          /> -->
        </div>
        <TransitionRoot
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <ComboboxOptions
            :class="dropDownClass"
            :hold="false"
            class="absolute z-10 w-full py-1 mt-1 overflow-auto text-base border rounded-md shadow-lg bg-theme-paper max-h-52 max-w-72 focus:outline-none sm:text-sm border-theme-divider"
          >
            <div v-if="loading" class="flex items-center justify-center p-2">
              <LoadingSpinner :height="24" :width="24" />
            </div>
            <div
              v-if="networkError"
              class="relative px-4 py-2 text-red-500 cursor-default select-none"
            >
              {{ networkError }}
            </div>

            <ComboboxOption
              v-for="(item, index) in fetchSuggestions && optionsExist
                ? fetchedOptions
                : filteredOptions"
              as="template"
              :key="index"
              v-slot="{ selected, active }"
              :value="item"
            >
              <li
                class="relative py-2 pl-5 pr-4 m-1 transition-all rounded-md cursor-pointer select-none hover:bg-theme-default"
                :class="{
                  // 'bg-primary-8': active,
                  'text-theme-primary': !active,
                }"
              >
                <slot name="item" :option="item">
                  <span
                    class="block truncate"
                    :class="{
                      'font-bold': selected || item[itemValue] === model?.slug,
                      'font-normal': !selected && item[itemValue] !== model?.slug,
                    }"
                  >
                    {{ item[itemLabel] }}
                  </span>
                </slot>
              </li>
            </ComboboxOption>
            <slot name="footer"></slot>
          </ComboboxOptions>
        </TransitionRoot>
      </div>
    </Combobox>
    <div class="mt-1 text-xs font-normal leading-none text-danger-500">
      <slot name="error">{{ changeErrorMessage(error) }}</slot>
    </div>
    <div
      class="mt-1 text-xs font-normal leading-none text-primary-500 dark:text-indigo-400"
    >
      <slot name="hint">{{ hint }}</slot>
    </div>
    <div class="mt-1 text-xs font-normal leading-none text-success-600 dark:text-green-400">
      <slot name="confirmation">{{ confirmation }}</slot>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  TransitionRoot,
} from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/20/solid';
import { useLoading } from '@/Composables/use-loading/useLoading';
import LoadingSpinner from '@/Components/LoadingSpinner.vue';
import { useDebounceFn } from '@vueuse/core';

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  options: {
    type: Object,
    required: false,
    default: [],
  },
  label: {
    type: String,
    required: false,
    default: null,
  },
  placeholder: {
    type: String,
    required: false,
    default: null,
  },
  required: {
    type: Boolean,
    required: false,
    default: false,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  returnObject: {
    type: Boolean,
    required: false,
    default: false,
  },
  itemLabel: {
    type: [String],
    required: false,
    default: 'name',
  },
  itemValue: {
    type: [String],
    required: false,
    default: 'id',
  },
  fetchSuggestions: {
    type: Function,
    required: false,
    default: null,
  },
  dropDownClass: {
    type: String,
    required: false,
    default: null,
  },
  getSuggestions: {
    type: Boolean,
    required: false,
    default: false,
  },
  showDropdownIcon: {
    type: Boolean,
    required: false,
    default: true,
  },
  theme: {
    type: String,
    required: false,
    default: 'default',
  },
  hint: {
    type: String,
    required: false,
  },
  error: {
    type: String,
    required: false,
  },
  confirmation: {
    type: String,
    required: false,
  },
  selectedLabel: {
    type: String,
    required: false,
    default: '',
  },
  parentClass: {
    type: String,
    required: false,
    default: null,
  },
  callQuerySearch: {
    type: Function,
    default: () => {},
  },
});

const emits = defineEmits(['change']);

const model = defineModel();
const { loading, showLoader, hideLoader } = useLoading();

const networkError = ref('');
let query = ref('');
let fetchedOptions = ref([]);
const firstRequest = ref(true);

const toDisplayString = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (typeof value === 'object') {
    const locale = document?.documentElement?.lang || 'en';
    const candidate = value[locale] ?? Object.values(value).find((v) => typeof v === 'string');

    return typeof candidate === 'string' ? candidate : '';
  }

  return '';
};

const selectedLabelText = ref(
  toDisplayString(props.selectedLabel) ||
    toDisplayString(
      model.value && typeof model.value === 'object'
        ? model.value[props.itemLabel]
        : model.value
    )
);

const resolveDisplayValue = (item) => {
  const fromItem = item && typeof item === 'object' ? item[props.itemLabel] : null;

  return toDisplayString(fromItem ?? selectedLabelText.value);
};

const inputClasses = {
  default:
    'h-10 ring-0 has-[:focus]:!border-theme-main has-[:focus]:border-theme-main',
  transparent:
    '!bg-inherit shadow-none ring-transparent has-[:focus]:border-theme-main',
};

const setError = (val) => {
  networkError.value = val;
};

let filteredOptions = computed(() => {
  return query.value === '' || query.value === undefined
    ? props.options
    : props.options.filter((item) =>
        item[props.itemLabel]
          ?.toLowerCase()
          ?.replace(/\s+/g, '')
          ?.includes(query.value.toLowerCase().replace(/\s+/g, ''))
      );
});

const optionsExist = computed(() => {
  return props.fetchSuggestions ? fetchedOptions.value?.length : filteredOptions.value?.length;
});

const remoteCallback = (items) => {
  fetchedOptions.value = items;
  hideLoader();
};

const handleInput = (event) => {
  let newValue = event.target.value;
  model.value = null;
  query.value = newValue;
  selectedLabelText.value = newValue;
  emits('change', newValue);

  if (props.fetchSuggestions && newValue) {
    fetchRemote(event);
  }
};

const itemSelected = (value) => {
  if (value && typeof value === 'object') {
    model.value = value;
    query.value = value[props.itemLabel];
    selectedLabelText.value = value[props.itemLabel];
  } else {
    // Headless UI resets the combobox with `null` and only treats it as empty
    // while the value stays null — anything else makes it select the highlighted
    // option as soon as the field loses focus.
    model.value = null;
    selectedLabelText.value = query.value;
  }
};

const errorCallback = (errorMessage = 'Something went wrong!') => {
  hideLoader();
  setError(errorMessage);
};

const fetchRemote = useDebounceFn((event) => {
  showLoader();
  setError('');
  firstRequest.value = false;
  fetchedOptions.value = [];
  props.fetchSuggestions(event?.target?.value, remoteCallback, errorCallback);
}, 200);

const changeErrorMessage = (text) => {
  if (/items\.\d+./.test(text)) {
    return text.replace(/items\.\d+./, '');
  } else {
    return text;
  }
};
watch(
  () => props.selectedLabel,
  (newLabel) => {
    selectedLabelText.value = toDisplayString(newLabel);
    if (!newLabel) {
      query.value = '';
    }
  }
);

watch(model, (newModel) => {
  if (newModel && typeof newModel === 'object') {
    selectedLabelText.value = toDisplayString(newModel[props.itemLabel]);
  }
});
</script>
